import { i18n } from "@/libs/wordpress.facade";
import { computed, reactive, readonly, ref, shallowRef, toRaw, toRef, toValue, watch, watchEffect, type ComputedRef, type MaybeRef, type Ref, type ShallowRef } from "vue";

export type InputConfig = {
	key: string;
	apiFormKey?: string;
	label?: string;
	default?: string;
	required?: boolean | ((formData: Record<string, any>) => boolean);
	placeholder?: string;
	type?: string;
	options?: Record<string, string>;
	depends_on_field_key?: string;
	depends_on_field_value?: string;
}

export type InputSelectOption = {label: string; value: string}

export type InputController = {
	key: string;
	value: Ref<unknown>;
	label: ComputedRef<string|undefined>;
	inputAttrs: ComputedRef<Record<string, unknown>>;
	selectOptions: Ref<InputSelectOption[]>;
	inputType: ComputedRef<string|undefined>;
	hasError: ComputedRef<boolean>;
	skip: ComputedRef<boolean>;
	getConfig: () => InputConfig;
	setConfig: (newConfig: InputConfig) => void;
	getApiFormDataValue: () => any;
	getApiFormDataKey: () => string;
}

export function useFormInputController(key: string, initialConfig: MaybeRef<InputConfig>, form: any): InputController{

	const inputConfig = toRef(initialConfig);
	const disabled = computed(() => {
		return form.disabled.value;
	});
	const hasError = computed(() => {
		return form.errors.value[key];
	});
	const value = ref(toValue(inputConfig.value.default));

	const label = computed(() => {
		return inputConfig.value.label;
	});

	const inputType = computed(() => {
		return inputConfig.value.type;
	});

	const required = computed(() => {
		if(inputConfig.value.depends_on_field_key){
			return toValue(form.rawFormData)?.[inputConfig.value.depends_on_field_key] == inputConfig.value.depends_on_field_value;
		}
		if(typeof(inputConfig.value.required) == 'boolean') return inputConfig.value.required;
		if(typeof(inputConfig.value.required) == 'function') return inputConfig.value.required(form.rawFormData);
		return false;
	});

	const skip = computed(() => {
		if(inputConfig.value.depends_on_field_key){
			const depFieldValue = toValue(toValue(form.controllers)?.[inputConfig.value.depends_on_field_key].value);
			return depFieldValue != inputConfig.value.depends_on_field_value;
		}
		return false;
	});

	const inputAttrs = computed(() => {
		return {
			disabled: Boolean(toValue(disabled)),
			required: Boolean(toValue(required)),
			placeholder: inputConfig.value.placeholder
		};
	});

	const selectOptions = computed(() => {
		if(!inputConfig.value.options) return [];
		return Object.entries(inputConfig.value.options).map(([value, label]) => {
			return {
				label,
				value 
			}
		});
	});

	function setConfig(newOptions: MaybeRef<InputConfig>){
		inputConfig.value = toValue(newOptions);
	}

	function getConfig(){
		return inputConfig.value;
	}

	function getApiFormDataKey(){
		return inputConfig.value.apiFormKey || inputConfig.value.key;
	}

	function getApiFormDataValue(){
		return value.value;
	}

	return {
		key,
		value,
		label,
		inputAttrs,
		selectOptions,
		inputType,
		hasError,
		skip,
		getConfig,
		setConfig,
		getApiFormDataValue,
		getApiFormDataKey
	}
}

export default function(){

	const disabled = ref(false);
	const controllers = shallowRef<Record<string, InputController>>({});

	const errors = shallowRef<Record<string, string>>({});

	const rawFormData = computed(() => {
		const raw: Record<string, any> = {};
		for (const [key, controller] of Object.entries(controllers.value)){
			if(!controller.skip.value) raw[key] = toValue(controller.value);
		}

		return raw;
	});

	function getApiFormData(){
		const raw: Record<string, any> = {};
		for (const [key, controller] of Object.entries(controllers.value)){
			raw[controller.getApiFormDataKey()] = toValue(controller.getApiFormDataValue());
		}

		return raw;
	}

	function validate(){
		const formErrors: Record<string, string> = {};

		for (const [key, controller] of Object.entries(controllers.value)){
			if(controller.inputAttrs.value.required && !controller.value.value){
				formErrors[key] = i18n.sprintf(i18n.__('%s obbligatorio', 'italpreziosi'), controller.label);
			}
		}

		errors.value = formErrors;

		return Object.keys(formErrors).length == 0;
	}

	const _form = {
		disabled,
		rawFormData,
		controllers,
		errors,
		validate,
		getApiFormData
	};
	
	/**
	 * 
	 * @param options 
	 * @param force when true always recreate input controllers also if exists for same input key
	 */
	function build(options: {inputs: InputConfig[]}, force?: boolean){
		const newControllers: Record<string, any> = {};
		for (const input of options.inputs) {
			if(!input.key) continue;
			if(!force && controllers.value[input.key]){
				controllers.value[input.key].setConfig(input);
				newControllers[input.key] = controllers.value[input.key];
			}else{
				newControllers[input.key] = useFormInputController(input.key, input, _form);
			}
		}
		controllers.value = newControllers;
	};

	return {
		build,
		..._form
	};
}